import { notifyDialogTransfer } from '@utils/operatorNotificationRuntime'
import { toastStore } from '@store/ToastStore'
import { t } from '@store/I18nStore'

const seenNotificationKeys = new Map<string, number>()
const locallyHandledTransferRequestIds = new Set<number>()
const locallySubmittedTransferChatIds = new Set<string>()

const DEDUPE_MS = 60_000
const LOCAL_ACTION_TTL_MS = 15_000
/** Transfer request lifetime per API (seconds). */
export const TRANSFER_PENDING_TTL_SEC = 60

export function defaultTransferExpiresAt(fromMs: number = Date.now()): string {
  return new Date(fromMs + TRANSFER_PENDING_TTL_SEC * 1000).toISOString()
}

/** Use backend expiresAt when it is still in the future; otherwise fall back to 60s TTL. */
function normalizeExpiresAt(raw: unknown): string {
  const s = String(raw ?? '').trim()
  if (!s) return defaultTransferExpiresAt()
  const t = new Date(s).getTime()
  if (!Number.isFinite(t) || t <= Date.now()) return defaultTransferExpiresAt()
  return new Date(t).toISOString()
}

export type IncomingTransferRequest = {
  requestId: number
  chatId: number
  chatTitle: string
  fromOperatorId: number
  fromOperatorName: string
  expiresAt: string
  text: string
}

export type OutgoingTransferPending = {
  requestId: number
  chatId: number
  chatTitle: string
  toOperatorId: number
  toOperatorName: string
  expiresAt: string
  text: string
}

export type TransferNotificationStore = {
  addIncoming: (req: IncomingTransferRequest) => boolean
  setOutgoingPending: (
    chatId: string,
    pending: OutgoingTransferPending & { createdAtMs?: number },
  ) => void
  removeByRequestId: (requestId: number) => void
  removeByChatId: (chatId: string) => void
  clearOutgoingPending: (chatId: string) => void
  clearOutgoingByRequestId: (requestId: number) => void
  setPendingOpenChatId: (chatId: string | null) => void
}

function coerceOperatorId(value: unknown): number | null {
  const id = Number(value)
  return Number.isFinite(id) && id > 0 ? id : null
}

function isOperatorId(operatorId: unknown, me: number): boolean {
  const id = coerceOperatorId(operatorId)
  return id != null && id === me
}

function resolveTransferRequestId(payload: Record<string, unknown>): number {
  return extractTransferRequestId(payload)
}

/**
 * Pull transfer request id from POST /transfer or WS payloads.
 * Backends vary: requestId, request_id, id, or nested transfer/request objects.
 */
export function extractTransferRequestId(raw: unknown): number {
  if (raw == null) return 0
  if (typeof raw === 'number' || typeof raw === 'string') {
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? n : 0
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) return 0
  const payload = raw as Record<string, unknown>
  const direct = [
    payload.requestId,
    payload.request_id,
    payload.transferRequestId,
    payload.transfer_request_id,
    payload.id,
  ]
  for (const value of direct) {
    const n = Number(value)
    if (Number.isFinite(n) && n > 0) return n
  }
  for (const key of ['transfer', 'transferRequest', 'transfer_request', 'request', 'data']) {
    const nested = payload[key]
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const n = extractTransferRequestId(nested)
      if (n > 0) return n
    }
  }
  return 0
}

export function buildIncomingTransferRequest(
  payload: Record<string, unknown>,
): IncomingTransferRequest | null {
  const requestId = resolveTransferRequestId(payload)
  const chatId = Number(payload.chatId ?? payload.chat_id)
  if (!Number.isFinite(requestId) || requestId <= 0) return null
  return {
    requestId,
    chatId: Number.isFinite(chatId) ? chatId : 0,
    chatTitle: String(payload.chatTitle ?? payload.chat_title ?? ''),
    fromOperatorId: Number(payload.fromOperatorId ?? payload.from_operator_id ?? 0),
    fromOperatorName: String(payload.fromOperatorName ?? payload.from_operator_name ?? ''),
    expiresAt: normalizeExpiresAt(payload.expiresAt ?? payload.expires_at),
    text: String(payload.text ?? ''),
  }
}

export function buildOutgoingTransferPending(
  payload: Record<string, unknown>,
  chatId: string,
): OutgoingTransferPending | null {
  const requestId = resolveTransferRequestId(payload)
  const chatIdNum = Number(payload.chatId ?? payload.chat_id ?? chatId)
  const toOperatorId = Number(payload.toOperatorId ?? payload.to_operator_id ?? 0)
  const toOperatorName = String(payload.toOperatorName ?? payload.to_operator_name ?? '')
  if (!chatId) return null
  return {
    requestId: Number.isFinite(requestId) && requestId > 0 ? requestId : 0,
    chatId: Number.isFinite(chatIdNum) ? chatIdNum : Number(chatId),
    chatTitle: String(payload.chatTitle ?? payload.chat_title ?? ''),
    toOperatorId,
    toOperatorName,
    expiresAt: normalizeExpiresAt(payload.expiresAt ?? payload.expires_at),
    text: String(
      payload.text ??
        (toOperatorName ? t('widget.client.waiting_operator') + ` ${toOperatorName}…` : t('widget.client.waiting_operator')),
    ),
  }
}

function playIncomingTransferSound(req: IncomingTransferRequest): void {
  notifyDialogTransfer({
    chatId: req.chatId > 0 ? String(req.chatId) : undefined,
    title: req.chatTitle || undefined,
    fromOperatorName: req.fromOperatorName || undefined,
    preview: req.text || undefined,
  })
}

/**
 * Apply transfer WS notification per product TZ:
 * - dialog.transfer.requested → new operator → accept/decline modal
 * - dialog.transfer.pending   → old operator → waiting banner (chat stays in Мои)
 * - accepted / declined / expired → clear UI state
 *
 * Backend already routes each event to the correct operator.${id}.notifications channel,
 * so we do not re-filter by toOperatorId for requested/pending.
 */
export function applyOperatorTransferNotification(
  raw: unknown,
  operatorId: number | null | undefined,
  store: TransferNotificationStore,
): string {
  const me = coerceOperatorId(operatorId)
  if (me == null) return ''

  const data = typeof raw === 'string' ? safeJsonParse(raw) : raw
  if (!data || typeof data !== 'object') return ''

  const { type, payload } = parseOperatorNotificationEvent(data)
  if (!type.startsWith('dialog.transfer.')) return ''

  const requestId = resolveTransferRequestId(payload)
  const chatIdRaw = payload.chatId ?? payload.chat_id
  const chatId = chatIdRaw != null && String(chatIdRaw) !== '' ? String(chatIdRaw) : ''
  const toOperatorId = payload.toOperatorId ?? payload.to_operator_id
  const fromOperatorId = payload.fromOperatorId ?? payload.from_operator_id

  // TZ: arrives on NEW operator's notifications channel → show accept modal
  if (type === 'dialog.transfer.requested') {
    // Ignore echo if we somehow receive our own submit on this channel
    if (wasTransferSubmittedLocally(chatId)) return type
    if (isOperatorId(fromOperatorId, me)) return type

    const req = buildIncomingTransferRequest(payload)
    if (!req) {
      console.warn('[transfer] requested without requestId', payload)
      return type
    }
    if (store.addIncoming(req)) {
      playIncomingTransferSound(req)
      toastStore.show(
        req.text ||
          (t('chat.transfer.incoming', { name: req.fromOperatorName || '' }) + (req.chatTitle ? ` ${req.chatTitle}` : '')).trim(),
        'info',
      )
    }
    return type
  }

  // TZ: arrives on OLD operator's notifications channel → waiting banner only
  if (type === 'dialog.transfer.pending') {
    if (!chatId) return type
    const outgoing = buildOutgoingTransferPending(payload, chatId)
    if (outgoing) store.setOutgoingPending(chatId, outgoing)
    return type
  }

  if (type === 'dialog.transfer.accepted') {
    if (Number.isFinite(requestId) && requestId > 0) store.removeByRequestId(requestId)
    if (chatId) {
      store.removeByChatId(chatId)
      store.clearOutgoingPending(chatId)
    }
    // New operator payload: { fromOperatorId, ... } (no toOperatorId)
    // Old operator payload: { toOperatorId, ... }
    // Open chat for the accepter (new operator).
    const iAmNewOperator =
      isOperatorId(toOperatorId, me) ||
      (coerceOperatorId(toOperatorId) == null &&
        coerceOperatorId(fromOperatorId) != null &&
        !isOperatorId(fromOperatorId, me))
    if (iAmNewOperator && chatId) {
      store.setPendingOpenChatId(chatId)
    }
    return type
  }

  if (type === 'dialog.transfer.declined' || type === 'dialog.transfer.expired') {
    if (Number.isFinite(requestId) && requestId > 0) {
      store.removeByRequestId(requestId)
      store.clearOutgoingByRequestId(requestId)
    }
    if (chatId) {
      store.removeByChatId(chatId)
      store.clearOutgoingPending(chatId)
    }
    return type
  }

  return type
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

function pruneExpiredKeys(now: number) {
  if (seenNotificationKeys.size < 32) return
  for (const [key, ts] of seenNotificationKeys) {
    if (now - ts > DEDUPE_MS) seenNotificationKeys.delete(key)
  }
}

/**
 * Parse operator notification events.
 * Supports TZ shape `{ type, payload: {...} }` and Centrifuge-flattened
 * `{ type, requestId, chatId, ... }` after unwrapCentrifugePublication.
 */
export function parseOperatorNotificationEvent(event: unknown): {
  type: string
  payload: Record<string, unknown>
} {
  if (!event || typeof event !== 'object') {
    return { type: '', payload: {} }
  }

  let root = event as Record<string, unknown>

  // Peel Centrifuge / gateway wrappers: { data: { type, payload } }
  for (let depth = 0; depth < 4; depth++) {
    const nested = root.data
    if (!nested || typeof nested !== 'object' || Array.isArray(nested)) break
    const inner = nested as Record<string, unknown>
    const outerType = root.type ?? root.event
    if (inner.type != null || inner.event != null || inner.payload != null || inner.requestId != null || inner.request_id != null) {
      root =
        inner.type == null && inner.event == null && outerType != null
          ? { ...inner, type: outerType }
          : inner
      continue
    }
    break
  }

  const type = String(root.type ?? root.event ?? '').toLowerCase()

  // TZ shape: { type, payload: { requestId, chatId, ... } }
  const nestedPayload = root.payload
  if (nestedPayload && typeof nestedPayload === 'object' && !Array.isArray(nestedPayload)) {
    const p = nestedPayload as Record<string, unknown>
    // If payload itself nests again, prefer inner fields but keep type from outer
    const inner = (p.payload ?? p.data) as Record<string, unknown> | undefined
    if (inner && typeof inner === 'object' && !Array.isArray(inner) && (inner.requestId != null || inner.request_id != null)) {
      return { type: type || String(p.type ?? '').toLowerCase(), payload: inner }
    }
    if (p.requestId != null || p.request_id != null || p.chatId != null || p.chat_id != null || p.text != null) {
      return { type, payload: p }
    }
  }

  // Flattened shape (after unwrapCentrifugePublication): fields on root
  if (type) {
    return { type, payload: root }
  }

  return { type: '', payload: {} }
}

export function buildOperatorNotificationDedupeKey(
  type: string,
  message: string,
  meta?: { requestId?: number; chatId?: string | number },
): string {
  const normalizedMessage = message.trim()
  const requestId = Number(meta?.requestId)
  const chatId = meta?.chatId != null ? String(meta.chatId) : ''

  if (type && Number.isFinite(requestId) && requestId > 0) {
    return `${type}:req:${requestId}`
  }
  if (type && normalizedMessage) {
    return `${type}:msg:${normalizedMessage}`
  }
  if (type && chatId) {
    return `${type}:chat:${chatId}`
  }
  return type || 'operator-notification'
}

export function consumeOperatorTransferNotification(
  type: string,
  message: string,
  meta?: { requestId?: number; chatId?: string | number },
): boolean {
  const requestId = Number(meta?.requestId)
  if (Number.isFinite(requestId) && requestId > 0 && locallyHandledTransferRequestIds.has(requestId)) {
    return false
  }

  const key = buildOperatorNotificationDedupeKey(type, message, meta)
  const now = Date.now()
  const prev = seenNotificationKeys.get(key)
  if (prev != null && now - prev < DEDUPE_MS) return false

  seenNotificationKeys.set(key, now)
  pruneExpiredKeys(now)
  return true
}

export function markTransferRequestHandledLocally(requestId: number) {
  if (!Number.isFinite(requestId) || requestId <= 0) return
  locallyHandledTransferRequestIds.add(requestId)
  window.setTimeout(() => {
    locallyHandledTransferRequestIds.delete(requestId)
  }, LOCAL_ACTION_TTL_MS)
}

export function markTransferSubmittedLocally(chatId: string) {
  if (!chatId) return
  locallySubmittedTransferChatIds.add(chatId)
  window.setTimeout(() => {
    locallySubmittedTransferChatIds.delete(chatId)
  }, LOCAL_ACTION_TTL_MS)
}

export function wasTransferSubmittedLocally(chatId: string): boolean {
  return locallySubmittedTransferChatIds.has(chatId)
}
